<template>
  <div id="pulseAvgScore">
    <div class="rightGraph">
      <div
        ref="chartdiv"
        class="hello"
        :class="emptyFlag ? 'dummyChart' : ''"
      ></div>
      <div
        v-if="emptyFlag"
        class="noData"
      >
        <div class="placeholder">
          {{ $t('pulse.noData') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themesAnimated);

export default {
  props: {
    chartData: {
      type: Array,
      default() {
        return [];
      },
    },

    emptyFlag: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loaded: false,
      chart: null,
    };
  },

  computed: {
    fontColor() {
      return getComputedStyle(document.getElementById('app')).getPropertyValue('--themeFontColor');
    },
  },

  watch: {
    chartData: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.disposeGraph();
          this.drawGraph();
        }
      },
    },
  },

  mounted() {
    this.disposeGraph();
    this.drawGraph();
  },

  beforeDestroy() {
    this.disposeGraph();
  },

  methods: {
    disposeGraph() {
      if (this.chart) {
        this.chart.dispose();
      }
    },

    drawGraph() {
      am4core.addLicense(process.env.VUE_APP_AMCHARTS_LICENSE);
      this.chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);
      this.chart.data = this.chartData;
      this.chart.colors.list = [
        am4core.color('#f3c065'),
        am4core.color('#00ace2'),
        am4core.color('#8d33be'),
      ];

      this.createX();
      this.createY();
      this.createSeries();
    },

    createX() {
      // Create X axes
      const categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis());

      // // Label
      categoryAxis.dataFields.category = 'pulse';
      categoryAxis.renderer.inversed = true;
      categoryAxis.renderer.labels.template.fill = am4core.color(this.fontColor);
      categoryAxis.renderer.labels.template.wrap = true;
      categoryAxis.renderer.labels.template.horizontalCenter = 'middle';
      categoryAxis.renderer.labels.template.verticalCenter = 'middle';
      categoryAxis.renderer.labels.template.dx = 7;
      categoryAxis.renderer.labels.template.adapter.add('text', (label, target) => {
        if (target.dataItem && label && !(label.includes('improvement') || label.includes('improvement'))) {
          /* eslint-disable */
          target.horizontalCenter = 'middle';
          target.rotation = -90;
          target.fontSize = 10;
          target.maxWidth = 1;
          target.maxHeight = 1;
          /* eslint-enable */
        }
        return label;
      });

      // // Grid
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.renderer.grid.template.stroke = am4core.color(this.fontColor);
      categoryAxis.renderer.grid.template.strokeOpacity = 0.5;
    },

    createY() {
      // Create Y axes
      const valueAxis = this.chart.xAxes.push(new am4charts.ValueAxis());

      // Grid
      valueAxis.min = 0;
      valueAxis.max = 5;
      valueAxis.renderer.minGridDistance = 60;
      valueAxis.renderer.maxGridDistance = 120;
      valueAxis.strictMinMax = true;

      // // Label
      valueAxis.renderer.labels.template.fill = am4core.color(this.fontColor);
      valueAxis.renderer.labels.template.wrap = true;
      valueAxis.renderer.labels.template.maxWidth = 110;
      valueAxis.renderer.labels.template.textAlign = 'middle';
      valueAxis.renderer.labels.template.fontSize = 10;
      valueAxis.renderer.labels.template.paddingRight = 40;
      valueAxis.renderer.labels.template.adapter.add('text', (label, target) => {
        const desc = {
          value0: '',
          value1: this.$t('pulse.score_1'),
          value2: this.$t('pulse.score_2'),
          value3: this.$t('pulse.score_3'),
          value4: this.$t('pulse.score_4'),
          value5: this.$t('pulse.score_5'),
        };
        if (target.dataItem && desc[`value${target.dataItem.value}`]) {
          return `${label.charAt(0)}\n${desc[`value${target.dataItem.value}`]}`;
        }
        return '';
      });

      valueAxis.renderer.baseGrid.stroke = am4core.color(this.fontColor);
      valueAxis.renderer.baseGrid.strokeOpacity = 1;
      valueAxis.renderer.baseGrid.strokeWidth = 1.5;

      // valueAxis.renderer.grid.template.stroke = am4core.color('#fff');
      valueAxis.renderer.grid.template.strokeOpacity = 0;
      // valueAxis.renderer.grid.template.strokeDasharray = 3;
    },

    createSeries() {
      const series = this.chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueX = 'score';
      series.dataFields.categoryY = 'pulse';
      series.name = 'score';
      series.columns.template.tooltipText = '{categoryY}: [bold]{valueX}[/]';
      series.tooltip.background.strokeOpacity = 0;
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color('white');
      series.tooltip.label.fill = am4core.color('black');
      series.columns.template.fillOpacity = 1;

      series.columns.template.adapter.add('fill', (fill, target) => this.chart.colors.getIndex(target.dataItem.index));

      const columnTemplate = series.columns.template;
      columnTemplate.strokeWidth = 1;
      columnTemplate.strokeOpacity = 0.6;
    },
  },
};
</script>

<style lang="scss" scoped>
#pulseAvgScore {
  .rightGraph {
    .hello {
      position: relative;
      width: 100%;
      height: 300px;
      font-style: normal;
      margin-left: -40px;

      &.dummyChart {
        filter: blur(2px) alpha(0.5);
      }

      &::before {
        position: absolute;
        content: '';
        top: calc(5% - 1px);
        left: 78px;
        width: 21px;
        height: calc(100% - 85px);
        background: rgba(255, 255, 255, 0.2);
        box-sizing: border-box;
      }
    }

    .bottomLabel {
      position: relative;
      display: flex;
      justify-content: space-around;
      font-size: 12px;
    }

    .noData {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 92%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>
